import { useEffect, useRef } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { logToNewRelic } from '../../services/debug';
import { useStores } from '../../stores/useStores';
import Button from '../buttons/Button';
import { Loader } from '../Loader/Loader';

import classes from './GameIframe.module.scss';
import { useGameIframeEvents } from './hooks/useGameIframeEvents';

const GameIframe = observer(() => {
  const { gameStore } = useStores();
  const { t } = useTranslation();
  const location = useLocation();

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const { isIframeLoading, onGameIframeError, retryIframeLoad } = useGameIframeEvents({ iframeRef });

  const { gameUiUrl, isGameUiError } = gameStore;

  const isInGamePath = location.pathname.startsWith('/games');

  useEffect(() => {
    if (!gameUiUrl) {
      void logToNewRelic(
        'GameIframe rendered null due to empty gameUiUrl',
        {
          gameUiFlag: gameStore.gameUiFlag,
          isGameUiError: gameStore.isGameUiError,
          currentGameId: gameStore.currentGame?.gameId,
          pathname: location.pathname,
          isInGamePath,
        },
        'warn',
      );
    }
  }, [
    gameUiUrl,
    gameStore.gameUiFlag,
    gameStore.isGameUiError,
    gameStore.currentGame?.gameId,
    location.pathname,
    isInGamePath,
  ]);

  if (!gameUiUrl) {
    return null;
  }

  if (isGameUiError) {
    return (
      <div
        className={classNames(classes.iframeWrapper, {
          [classes.hidden]: !isInGamePath,
        })}
      >
        <div className={classes.errorWrapper}>
          <p className={classes.errorMessage}>{t('game.gameWasUnableToLoad')}</p>
          <Button btnType="main" handleOnClick={retryIframeLoad}>
            {t('common.tryAgainUpperFIrstLetter')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames(classes.iframeWrapper, {
        [classes.hidden]: !gameStore.showGameUi || !isInGamePath,
      })}
    >
      {(isIframeLoading || !gameStore.slotsGameUiIframe) && (
        <div className={classes.loaderWrapper}>
          <Loader fullScreen />
        </div>
      )}
      <iframe
        ref={iframeRef}
        allow="geolocation *; bluetooth *; accelerometer *; gyroscope *; web-share *; clipboard-write *; clipboard-read *"
        className={classes.iframe}
        data-testid="game-ui-iframe"
        height="100%"
        src={gameUiUrl}
        style={{ border: 'none' }}
        width="100%"
        onError={onGameIframeError}
      />
    </div>
  );
});

export default GameIframe;
